import { useEffect, useRef, useState } from "react";
import {
  Terminal,
  Loader2,
  Download,
  X,
  ChevronDown,
  ChevronUp,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export type LogType = "info" | "success" | "error" | "stage";

export interface LogEntry {
  time: string;
  type: LogType;
  message: string;
  debug?: Record<string, unknown>;
}

interface FloatingActivityLogProps {
  logs: LogEntry[];
  isAnyPending: boolean;
  latestRunId?: string | null;
  isDebugCollectorActive?: boolean;
  onClear: () => void;
  onDownloadDebugRun?: (runId: string) => void;
}

const LS_KEY = "pbnsd:existing-posts:floating-log:minimized";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-5 px-1.5 text-[10px]"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
    >
      <Copy className="h-2.5 w-2.5 mr-1" />
      {copied ? "✓" : "Kopiuj"}
    </Button>
  );
}

function DebugPanel({ debug }: { debug: Record<string, unknown> }) {
  const compiledPrompt =
    typeof debug.compiled_prompt === "string" ? debug.compiled_prompt : null;
  const responseText =
    typeof debug.response_text === "string" ? debug.response_text : null;
  const rest: Record<string, unknown> = { ...debug };
  if (compiledPrompt) delete rest.compiled_prompt;
  if (responseText) delete rest.response_text;

  return (
    <div className="ml-6 mt-1 mb-2 space-y-2">
      {compiledPrompt && (
        <div className="rounded bg-muted/30 border">
          <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/50">
            <span className="text-[10px] font-semibold text-muted-foreground">
              📝 Prompt ({compiledPrompt.length} znaków)
            </span>
            <CopyButton text={compiledPrompt} />
          </div>
          <pre className="p-2 text-[10px] text-muted-foreground whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
            {compiledPrompt}
          </pre>
        </div>
      )}
      {responseText && (
        <div className="rounded bg-muted/30 border">
          <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/50">
            <span className="text-[10px] font-semibold text-muted-foreground">
              📬 Response ({responseText.length} znaków)
            </span>
            <CopyButton text={responseText} />
          </div>
          <pre className="p-2 text-[10px] text-muted-foreground whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
            {responseText}
          </pre>
        </div>
      )}
      {Object.keys(rest).length > 0 && (
        <div className="rounded bg-muted/30 border">
          <div className="flex items-center justify-between px-2 py-1 border-b bg-muted/50">
            <span className="text-[10px] font-semibold text-muted-foreground">
              🔍 Debug data
            </span>
            <CopyButton text={JSON.stringify(rest, null, 2)} />
          </div>
          <pre className="p-2 text-[10px] text-muted-foreground whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
            {JSON.stringify(rest, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export function FloatingActivityLog({
  logs,
  isAnyPending,
  latestRunId,
  isDebugCollectorActive = false,
  onClear,
  onDownloadDebugRun,
}: FloatingActivityLogProps) {
  const [minimized, setMinimized] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(LS_KEY) === "1";
  });
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const endRef = useRef<HTMLDivElement | null>(null);

  // Auto-expand when new operation starts
  useEffect(() => {
    if (isAnyPending && minimized) {
      setMinimized(false);
      window.localStorage.setItem(LS_KEY, "0");
    }
  }, [isAnyPending, minimized]);

  // Persist minimized state
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LS_KEY, minimized ? "1" : "0");
  }, [minimized]);

  // Auto-scroll to latest log
  useEffect(() => {
    if (!minimized && endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [logs.length, minimized]);

  if (logs.length === 0) return null;

  const handleDownloadTxt = () => {
    const txt = logs
      .map((log) => {
        let line = `[${log.time}] [${log.type.toUpperCase()}] ${log.message}`;
        if (log.debug) {
          line +=
            "\n" +
            JSON.stringify(log.debug, null, 2)
              .split("\n")
              .map((l) => "    " + l)
              .join("\n");
        }
        return line;
      })
      .join("\n");
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `existing_posts_log_${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/:/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleLogExpand = (idx: number) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Minimized: tylko mały pasek
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-30 flex items-center gap-2 px-3 py-2 rounded-lg bg-background border-2 border-violet-500/30 shadow-lg hover:border-violet-500/60 transition-colors text-xs font-medium"
        aria-label="Rozwiń dziennik operacji"
      >
        <Terminal className="h-4 w-4 text-violet-500" />
        <span>Dziennik · {logs.length}</span>
        {isAnyPending && <Loader2 className="h-3 w-3 animate-spin text-amber-500" />}
        <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    );
  }

  // Expanded: floating panel bottom-right
  return (
    <div className="fixed bottom-4 right-4 z-30 w-[460px] max-w-[calc(100vw-2rem)] rounded-lg border-2 border-violet-500/30 bg-background shadow-2xl overflow-hidden flex flex-col max-h-[60vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Terminal className="h-4 w-4 text-violet-500" />
          Dziennik operacji ({logs.length})
          {isAnyPending && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={handleDownloadTxt}
            title="Pobierz log jako TXT"
          >
            TXT
          </Button>
          {isDebugCollectorActive && latestRunId && onDownloadDebugRun && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onDownloadDebugRun(latestRunId)}
              title="Pełen TXT bieżącego runu (debug)"
            >
              <Download className="h-3 w-3 mr-1" />
              Debug
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onClear}
            title="Wyczyść log"
          >
            Wyczyść
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setMinimized(true)}
            title="Zwiń"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
          {!isAnyPending && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={onClear}
              title="Zamknij (czyści log)"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Logs list */}
      <div className="flex-1 overflow-y-auto p-3 bg-background font-mono text-xs space-y-0.5">
        {logs.map((log, i) => (
          <div key={i}>
            <div
              className={`flex gap-2 ${
                log.debug ? "cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1" : ""
              } ${
                log.type === "error"
                  ? "text-red-500 dark:text-red-400"
                  : log.type === "success"
                    ? "text-green-600 dark:text-green-400"
                    : log.type === "stage"
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-muted-foreground"
              }`}
              onClick={() => log.debug && toggleLogExpand(i)}
            >
              <span className="text-muted-foreground/60 shrink-0">{log.time}</span>
              <span className="flex-1 break-words">{log.message}</span>
              {log.debug && (
                <span className="text-muted-foreground/40 shrink-0">
                  {expandedLogs.has(i) ? "▾" : "▸"}
                </span>
              )}
            </div>
            {log.debug && expandedLogs.has(i) && <DebugPanel debug={log.debug} />}
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
}
